/**
 * L2-S4: moved verbatim out of `src/app/shared/services/excel-export.helpers.ts` (now a shim) with
 * `ExcelSharedService`, its only caller. Pure functions over a SheetJS worksheet.
 */
import * as XLSX from 'xlsx';

const CONTACT_DETAILS_EXPORT_HEADERS = ['Primary Contact Details', 'Landlord Details'];
const MIN_CONTACT_DETAILS_EXCEL_COLUMN_WIDTH = 24;
const MAX_CONTACT_DETAILS_EXCEL_COLUMN_WIDTH = 60;
const CONTACT_DETAILS_EXCEL_COLUMN_PADDING = 2;

export function getContactDetailsColumnIndexes(headerRow: readonly unknown[]): number[] {
  return headerRow.reduce<number[]>((acc, header, index) => {
    const normalizedHeader = typeof header === 'string' ? header.trim() : String(header ?? '').trim();
    if (CONTACT_DETAILS_EXPORT_HEADERS.includes(normalizedHeader)) acc.push(index);
    return acc;
  }, []);
}

export function applyContactDetailsColumnWidths(
  sourceWorkSheet: XLSX.WorkSheet,
  contactDetailsColumnIndexes: number[]
): void {
  const sourceWorksheetRef = sourceWorkSheet['!ref'];
  if (!sourceWorksheetRef || !contactDetailsColumnIndexes.length) return;
  const sourceRange = XLSX.utils.decode_range(sourceWorksheetRef);

  sourceWorkSheet['!cols'] = sourceWorkSheet['!cols'] ?? Array.from({ length: sourceRange.e.c + 1 });
  contactDetailsColumnIndexes.forEach(columnIndex => {
    if (columnIndex < sourceRange.s.c || columnIndex > sourceRange.e.c) return;
    sourceWorkSheet['!cols'][columnIndex] = { wch: getContactDetailsColumnWidth(sourceWorkSheet, sourceRange, columnIndex) };
  });
}

function getContactDetailsColumnWidth(
  sourceWorkSheet: XLSX.WorkSheet,
  sourceRange: XLSX.Range,
  columnIndex: number
): number {
  const maxLineLength = Array.from({ length: sourceRange.e.r - sourceRange.s.r + 1 }, (_, index) => {
    const cellAddress = XLSX.utils.encode_cell({ r: sourceRange.s.r + index, c: columnIndex });
    return getCellMaxLineLength(sourceWorkSheet[cellAddress]);
  }).reduce((maxLength, cellLength) => Math.max(maxLength, cellLength), 0);

  return Math.min(
    MAX_CONTACT_DETAILS_EXCEL_COLUMN_WIDTH,
    Math.max(MIN_CONTACT_DETAILS_EXCEL_COLUMN_WIDTH, maxLineLength + CONTACT_DETAILS_EXCEL_COLUMN_PADDING)
  );
}

function getCellMaxLineLength(cell?: XLSX.CellObject): number {
  const cellValue = cell?.w ?? cell?.v ?? '';
  return String(cellValue)
    .split(/\r?\n/)
    .reduce((maxLength, line) => Math.max(maxLength, line.trimEnd().length), 0);
}
